// Drawer primitive. Canonical layout for tall content (per CLAUDE.md):
//
//   <Drawer open={...}>
//     <DrawerContent>
//       <DrawerHeader>title + description</DrawerHeader>
//       <DrawerBody>scrollable form / lists / etc</DrawerBody>
//       <DrawerFooter>action buttons (always visible)</DrawerFooter>
//     </DrawerContent>
//   </Drawer>
//
// `DrawerContent` caps height to `95dvh` and is `flex flex-col overflow-hidden` by default.
// `DrawerBody` is `flex-1 overflow-y-auto` so it absorbs the remaining height and scrolls
// when content overflows. The header and footer sit on either side and never scroll, so the
// action buttons are guaranteed to remain visible regardless of how tall the content grows.
//
// `dvh` (dynamic viewport height) is used over `vh` so mobile browser chrome appearing/
// disappearing doesn't push the footer below the address bar.

import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "@/lib/utils"

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
Drawer.displayName = "Drawer"

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-black/80", className)}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

// vaul's Content onPointerDown always runs its drag `onPress` — `setIsDragging(true)` plus
// `setPointerCapture()` on the pressed element — and its SELECT guard only applies once the
// pointer MOVES. On iOS Safari that press handling dismisses a native <select> picker the instant
// it opens. Stopping pointerdown on selects in the capture phase keeps it from reaching vaul's
// bubble handler; the select's native behaviour (opening the picker) is unaffected.
const stopDragOnNativeSelect = (event: React.PointerEvent<HTMLDivElement>) => {
  if (event.target instanceof HTMLSelectElement) event.stopPropagation()
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, onPointerDownCapture, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      data-slot="drawer-content"
      onPointerDownCapture={(event) => {
        onPointerDownCapture?.(event)
        stopDragOnNativeSelect(event)
      }}
      className={cn(
        // Layout: pinned to bottom, capped at 95dvh, vertical flex column so header/body/footer
        // can compose into a header-scroll-footer pattern. `overflow-hidden` keeps children from
        // bleeding past the rounded top corners; DrawerBody handles its own internal scrolling.
        "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[95dvh] flex-col overflow-hidden rounded-t-[10px] border bg-background",
        className
      )}
      {...props}
    >
      <div className="mx-auto mt-4 h-2 w-[100px] flex-shrink-0 rounded-full bg-muted" />
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
))
DrawerContent.displayName = "DrawerContent"

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="drawer-header"
    className={cn("grid flex-shrink-0 gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
)
DrawerHeader.displayName = "DrawerHeader"

/**
 * Scrollable region of a Drawer. Drop between `DrawerHeader` and `DrawerFooter` (or alone if
 * those aren't needed). Takes `flex-1` so it absorbs remaining height inside the flex-column
 * `DrawerContent`, and `overflow-y-auto` so tall content scrolls inside instead of pushing
 * the footer off-screen.
 */
const DrawerBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="drawer-body"
    className={cn("flex-1 overflow-y-auto px-4 pb-2", className)}
    {...props}
  />
)
DrawerBody.displayName = "DrawerBody"

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="drawer-footer"
    className={cn("mt-auto flex flex-shrink-0 flex-col gap-2 p-4", className)}
    {...props}
  />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
